import moment from 'moment';
import DOMPurify from 'dompurify';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getPromptVersions, getPromptVersionDetail, restorePromptVersion } from '../../../store/Agent';
import './PromptHistoryDrawer.css';

const PromptHistoryDrawer = ({ isOpen, onClose, agentId }) => {
  const dispatch = useDispatch();
  const { items = [], pagination = {}, currentVersionNumber = 0, isLoading = false } = useSelector((state) => state['Agent']['promptVersions'] || {});
  const [ui, setUi] = useState({ page: 1, viewVersion: null, restoreVersion: null, isRestoring: false });
  const updateUi = (patch) => setUi((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (isOpen && agentId) {
      updateUi({ page: 1 });
      dispatch(getPromptVersions({ agentId, page: 1, limit: 20 }));
    }
  }, [isOpen, agentId, dispatch]);

  const handleLoadMore = () => {
    const nextPage = ui['page'] + 1;
    updateUi({ page: nextPage });
    dispatch(getPromptVersions({ agentId, page: nextPage, limit: 20 }));
  };

  const handleView = (version) => {
    dispatch(getPromptVersionDetail({ agentId, versionNumber: version['versionNumber'], successCallback: (fullVersion) => updateUi({ viewVersion: fullVersion }) }));
  };

  const handleConfirmRestore = () => {
    updateUi({ isRestoring: true });
    dispatch(restorePromptVersion({
      agentId,
      versionNumber: ui['restoreVersion']['versionNumber'],
      successCallback: () => updateUi({ isRestoring: false, restoreVersion: null, page: 1 }),
      failCallback: () => updateUi({ isRestoring: false }),
    }));
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} className="agent-prompt-history">
      <div className="agent-prompt-history-panel">
        <div className="agent-prompt-history-header">
          <h3>Prompt History</h3>
          <button className="agent-prompt-history-close-btn" onClick={onClose}><CloseIcon fontSize="small" /></button>
        </div>
        <div className="agent-prompt-history-list">
          {isLoading && items.length === 0 && <div className="agent-prompt-history-loading"><i className="fa fa-spinner fa-spin" /> Loading versions…</div>}
          {!isLoading && items.length === 0 && <p className="agent-prompt-history-empty">Versions are recorded when you save the prompt.</p>}
          {items.map((version) => (
            <div key={version['_id']} className="agent-prompt-history-row">
              <div className="agent-prompt-history-row-top">
                <span className="agent-prompt-history-number">v{version['versionNumber']}</span>
                <span className={`agent-prompt-history-badge agent-prompt-history-badge-${version['changeSource']}`}>{sourceLabel(version['changeSource'])}</span>
                {version['versionNumber'] === currentVersionNumber && <span className="agent-prompt-history-current-chip">• Current</span>}
              </div>
              <div className="agent-prompt-history-row-meta">
                <span>{authorName(version)}</span>
                <span>{moment(version['createdAt']).format('MMM D, YYYY h:mm A')}</span>
              </div>
              {version['changeSource'] === 'ai' && version['feedback'] && <p className="agent-prompt-history-feedback">“{version['feedback']}”</p>}
              {version['changeSource'] === 'revert' && version['revertedFromVersion'] && <p className="agent-prompt-history-revert-note">Restored from v{version['revertedFromVersion']}</p>}
              <p className="agent-prompt-history-preview">{version['preview']}</p>
              <div className="agent-prompt-history-row-actions">
                <button className="action-btn" onClick={() => handleView(version)} title="View full prompt"><i className="fa fa-eye" /></button>
                {version['versionNumber'] !== currentVersionNumber && (
                  <button className="action-btn" onClick={() => updateUi({ restoreVersion: version })} title="Restore this version"><i className="fa fa-undo" /></button>
                )}
              </div>
            </div>
          ))}
          {pagination['totalPages'] > ui['page'] && (
            <button className="btn-style-border agent-prompt-history-load-btn" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading && <i className="fa fa-spinner fa-spin icon" />} Load more
            </button>
          )}
        </div>
      </div>

      <Dialog open={!!ui['viewVersion']} onClose={() => updateUi({ viewVersion: null })} className="agent-prompt-history-view" maxWidth="md" fullWidth>
        <DialogTitle className="agent-prompt-history-view-title">
          Prompt v{ui['viewVersion']?.['versionNumber']}
          <button className="agent-prompt-history-close-btn" onClick={() => updateUi({ viewVersion: null })}><CloseIcon fontSize="small" /></button>
        </DialogTitle>
        <DialogContent>
          <div className="agent-prompt-history-view-content" dangerouslySetInnerHTML={{ __html: sanitizePromptHtml(ui['viewVersion']?.['script']) }} />
        </DialogContent>
        <DialogActions className="agent-prompt-history-view-actions">
          <button className="btn-style-border" onClick={() => updateUi({ viewVersion: null })}>Close</button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!ui['restoreVersion']} onClose={() => !ui['isRestoring'] && updateUi({ restoreVersion: null })} className="agent-prompt-history-confirm">
        <DialogTitle>Restore v{ui['restoreVersion']?.['versionNumber']}?</DialogTitle>
        <DialogContent>
          <p className="agent-prompt-history-confirm-text">This will replace the agent's current prompt. Your current prompt will be preserved as a new version, so you can always restore it back.</p>
        </DialogContent>
        <DialogActions className="agent-prompt-history-confirm-actions">
          <button className="btn-style-border" onClick={() => updateUi({ restoreVersion: null })} disabled={ui['isRestoring']}>Cancel</button>
          <button className="btn-style-new agent-prompt-history-restore-btn" onClick={handleConfirmRestore} disabled={ui['isRestoring']}>
            {ui['isRestoring'] && <i className="fa fa-spinner fa-spin icon" />} Restore
          </button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

function sourceLabel(changeSource) {
  if (changeSource === 'ai') return '✦ AI';
  if (changeSource === 'revert') return 'Revert';
  return 'Manual';
}

function authorName(version) {
  const author = version['userId'];
  if (!author) return 'Unknown';
  return `${author['firstName'] || ''} ${author['lastName'] || ''}`.trim() || 'Unknown';
}

const sanitizePromptHtml = (html) => DOMPurify.sanitize(html || '');

export default PromptHistoryDrawer;
